import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  subscriptionPoliciesApiService,
  SubscriptionPolicy,
  SubscriptionPolicyFilters,
  SubscriptionPolicyStats,
  CreateSubscriptionPolicyDto,
  UpdateSubscriptionPolicyDto,
} from '../api/subscription-policies-api';

// Estado de políticas de suscripción
export interface SubscriptionPoliciesState {
  policies: SubscriptionPolicy[];
  currentPolicy: SubscriptionPolicy | null;
  loading: boolean;
  error: string | null;
  updating: string[]; // IDs de políticas que están siendo actualizadas
  pagination: {
    page: number;
    limit: number;
    total: number;
  };
  filters: SubscriptionPolicyFilters;
  stats: SubscriptionPolicyStats;
}

// Función para calcular estadísticas basadas en las políticas
const calculateStats = (policies: SubscriptionPolicy[]): SubscriptionPolicyStats => {
  return {
    total: policies.length,
    active: policies.filter(p => p.status === 'active').length,
    inactive: policies.filter(p => p.status === 'inactive').length,
    draft: policies.filter(p => p.status === 'draft').length,
    withTrial: policies.filter(p => p.trialEnabled).length,
    withAutoRenewal: policies.filter(p => p.autoRenewal).length,
  };
};

// Estado inicial
const initialState: SubscriptionPoliciesState = {
  policies: [],
  currentPolicy: null,
  loading: false,
  error: null,
  updating: [],
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
  },
  filters: {},
  stats: {
    total: 0,
    active: 0,
    inactive: 0,
    draft: 0,
    withTrial: 0,
    withAutoRenewal: 0,
  },
};

// Async Thunks
export const fetchPolicies = createAsyncThunk(
  'subscriptionPolicies/fetchPolicies',
  async (filters: SubscriptionPolicyFilters = {}, { rejectWithValue }) => {
    try {
      const response = await subscriptionPoliciesApiService.getAllPolicies(filters);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al cargar políticas'
      );
    }
  }
);

export const fetchPolicy = createAsyncThunk(
  'subscriptionPolicies/fetchPolicy',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await subscriptionPoliciesApiService.getPolicy(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al cargar política'
      );
    }
  }
);

export const createPolicy = createAsyncThunk(
  'subscriptionPolicies/createPolicy',
  async (data: CreateSubscriptionPolicyDto, { rejectWithValue }) => {
    try {
      const response = await subscriptionPoliciesApiService.createPolicy(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al crear política'
      );
    }
  }
);

export const updatePolicy = createAsyncThunk(
  'subscriptionPolicies/updatePolicy',
  async (
    { id, data }: { id: string; data: UpdateSubscriptionPolicyDto },
    { rejectWithValue }
  ) => {
    try {
      const response = await subscriptionPoliciesApiService.updatePolicy(id, data);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al actualizar política'
      );
    }
  }
);

export const deletePolicy = createAsyncThunk(
  'subscriptionPolicies/deletePolicy',
  async (id: string, { rejectWithValue }) => {
    try {
      await subscriptionPoliciesApiService.deletePolicy(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al eliminar política'
      );
    }
  }
);

export const togglePolicyStatus = createAsyncThunk(
  'subscriptionPolicies/togglePolicyStatus',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await subscriptionPoliciesApiService.togglePolicyStatus(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al cambiar estado de la política'
      );
    }
  }
);

export const duplicatePolicy = createAsyncThunk(
  'subscriptionPolicies/duplicatePolicy',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await subscriptionPoliciesApiService.duplicatePolicy(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al duplicar política'
      );
    }
  }
);

export const setAsDefault = createAsyncThunk(
  'subscriptionPolicies/setAsDefault',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await subscriptionPoliciesApiService.setAsDefault(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al establecer política predeterminada'
      );
    }
  }
);

export const fetchPolicyStats = createAsyncThunk(
  'subscriptionPolicies/fetchPolicyStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await subscriptionPoliciesApiService.getPolicyStats();
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al cargar estadísticas'
      );
    }
  }
);

// Slice
const subscriptionPoliciesSlice = createSlice({
  name: 'subscriptionPolicies',
  initialState,
  reducers: {
    clearError: state => {
      state.error = null;
    },
    setCurrentPolicy: (state, action: PayloadAction<SubscriptionPolicy | null>) => {
      state.currentPolicy = action.payload;
    },
    setFilters: (state, action: PayloadAction<SubscriptionPolicyFilters>) => {
      state.filters = action.payload;
    },
    setPagination: (
      state,
      action: PayloadAction<{ page: number; limit: number; total: number }>
    ) => {
      state.pagination = action.payload;
    },
    clearPolicies: state => {
      state.policies = [];
      state.currentPolicy = null;
    },
  },
  extraReducers: builder => {
    builder
      // Fetch Policies
      .addCase(fetchPolicies.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPolicies.fulfilled, (state, action) => {
        state.loading = false;
        const policiesData = (action.payload as any)?.data || action.payload;
        state.policies = Array.isArray(policiesData) ? policiesData : [];
        state.pagination.total = state.policies.length;
        state.stats = calculateStats(state.policies);
      })
      .addCase(fetchPolicies.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch Policy
      .addCase(fetchPolicy.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPolicy.fulfilled, (state, action) => {
        state.loading = false;
        const policyData = (action.payload as any)?.data || action.payload;
        state.currentPolicy = policyData;
      })
      .addCase(fetchPolicy.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Create Policy
      .addCase(createPolicy.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createPolicy.fulfilled, (state, action) => {
        state.loading = false;
        const policyData = (action.payload as any)?.data || action.payload;
        if (!Array.isArray(state.policies)) {
          state.policies = [];
        }
        state.policies.push(policyData);
        state.pagination.total += 1;
        state.stats = calculateStats(state.policies);
      })
      .addCase(createPolicy.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Update Policy
      .addCase(updatePolicy.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePolicy.fulfilled, (state, action) => {
        state.loading = false;
        const policyData = (action.payload as any)?.data || action.payload;
        if (!Array.isArray(state.policies)) {
          state.policies = [];
        }
        const index = state.policies.findIndex(p => p.id === policyData.id);
        if (index !== -1) {
          state.policies[index] = policyData;
        }
        if (state.currentPolicy?.id === policyData.id) {
          state.currentPolicy = policyData;
        }
        state.stats = calculateStats(state.policies);
      })
      .addCase(updatePolicy.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Delete Policy
      .addCase(deletePolicy.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deletePolicy.fulfilled, (state, action) => {
        state.loading = false;
        if (!Array.isArray(state.policies)) {
          state.policies = [];
        }
        state.policies = state.policies.filter(p => p.id !== action.payload);
        state.pagination.total -= 1;
        if (state.currentPolicy?.id === action.payload) {
          state.currentPolicy = null;
        }
        state.stats = calculateStats(state.policies);
      })
      .addCase(deletePolicy.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Toggle Status
      .addCase(togglePolicyStatus.pending, (state, action) => {
        const policyId = action.meta.arg;
        if (!state.updating.includes(policyId)) {
          state.updating.push(policyId);
        }
        state.error = null;
      })
      .addCase(togglePolicyStatus.fulfilled, (state, action) => {
        const policyId = action.meta.arg;
        state.updating = state.updating.filter(id => id !== policyId);

        const policyData = (action.payload as any)?.data || action.payload;
        if (!Array.isArray(state.policies)) {
          state.policies = [];
        }
        const index = state.policies.findIndex(p => p.id === policyData.id);
        if (index !== -1) {
          state.policies[index] = policyData;
        }
        if (state.currentPolicy?.id === policyData.id) {
          state.currentPolicy = policyData;
        }
        state.stats = calculateStats(state.policies);
      })
      .addCase(togglePolicyStatus.rejected, (state, action) => {
        const policyId = action.meta.arg;
        state.updating = state.updating.filter(id => id !== policyId);
        state.error = action.payload as string;
      })
      // Duplicate Policy
      .addCase(duplicatePolicy.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(duplicatePolicy.fulfilled, (state, action) => {
        state.loading = false;
        const policyData = (action.payload as any)?.data || action.payload;
        if (!Array.isArray(state.policies)) {
          state.policies = [];
        }
        state.policies.push(policyData);
        state.pagination.total += 1;
        state.stats = calculateStats(state.policies);
      })
      .addCase(duplicatePolicy.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Set As Default
      .addCase(setAsDefault.pending, (state, action) => {
        const policyId = action.meta.arg;
        if (!state.updating.includes(policyId)) {
          state.updating.push(policyId);
        }
        state.error = null;
      })
      .addCase(setAsDefault.fulfilled, (state, action) => {
        const policyId = action.meta.arg;
        state.updating = state.updating.filter(id => id !== policyId);

        const policyData = (action.payload as any)?.data || action.payload;
        if (!Array.isArray(state.policies)) {
          state.policies = [];
        }
        // Actualizar todas las políticas: quitar isDefault de las demás
        state.policies = state.policies.map(p => ({
          ...p,
          isDefault: p.id === policyData.id,
        }));
        if (state.currentPolicy?.id === policyData.id) {
          state.currentPolicy = { ...policyData, isDefault: true };
        }
        state.stats = calculateStats(state.policies);
      })
      .addCase(setAsDefault.rejected, (state, action) => {
        const policyId = action.meta.arg;
        state.updating = state.updating.filter(id => id !== policyId);
        state.error = action.payload as string;
      })
      // Fetch Stats
      .addCase(fetchPolicyStats.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPolicyStats.fulfilled, (state, action) => {
        state.loading = false;
        const statsData = (action.payload as any)?.data || action.payload;
        state.stats = statsData;
      })
      .addCase(fetchPolicyStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  clearError,
  setCurrentPolicy,
  setFilters,
  setPagination,
  clearPolicies,
} = subscriptionPoliciesSlice.actions;

export default subscriptionPoliciesSlice.reducer;
